import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../utils/loadGoogleMaps";
import { formatDuration } from "../utils/formatDuration";
import { getOrderRoute } from "../api/client";

export default function RiderMapNavigation({ order, driverLocation }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const routePolylineRef = useRef(null);

  const [routeInfo, setRouteInfo] = useState(null);
  const [steps, setSteps] = useState([]);
  const [showSteps, setShowSteps] = useState(false);
  const [mapError, setMapError] = useState("");
  const [loading, setLoading] = useState(true);

  if (!order) return null;

  // Determine stage and target coordinates
  const isPickupStage = ["accepted", "arrived_at_pickup"].includes(order.status);
  const currentPendingStop = order.stops?.find((s) => s.status === "pending");
  const targetLocation = isPickupStage
    ? order.pickup
    : currentPendingStop || order.dropoff;

  const targetLabel = isPickupStage
    ? "Pickup Point"
    : currentPendingStop
    ? "Next Stop"
    : "Final Drop-off";

  const originLat = driverLocation?.lat || order.pickup.lat;
  const originLng = driverLocation?.lng || order.pickup.lng;

  // Open native GPS app (Google Maps / Apple Maps deep link)
  const openNativeNavigation = () => {
    const url = `https://www.google.com/maps/dir/?api=1&origin=${originLat},${originLng}&destination=${targetLocation.lat},${targetLocation.lng}&travelmode=driving`;
    window.open(url, "_blank");
  };

  useEffect(() => {
    let cancelled = false;

    async function initMap() {
      try {
        setLoading(true);
        setMapError("");
        const google = await loadGoogleMaps();

        if (cancelled || !mapRef.current) return;

        if (!mapInstanceRef.current) {
          const map = new google.maps.Map(mapRef.current, {
            center: { lat: originLat, lng: originLng },
            zoom: 14,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]
          });
          mapInstanceRef.current = map;

          new google.maps.Marker({
            position: { lat: originLat, lng: originLng },
            map,
            title: "You",
            label: { text: "🛵", fontSize: "20px" }
          });
          new google.maps.Marker({
            position: { lat: targetLocation.lat, lng: targetLocation.lng },
            map,
            title: targetLabel,
            label: { text: "📍", fontSize: "20px" }
          });
        }

        // Fetch the route from our backend (Routes API) rather than the
        // browser's DirectionsService, which calls the legacy Directions
        // API and is blocked on this project the same way Distance Matrix was.
        try {
          const { data: route } = await getOrderRoute(order._id, originLat, originLng);
          if (cancelled) return;
          setLoading(false);

          if (route?.polyline) {
            const decodedPath = google.maps.geometry.encoding.decodePath(route.polyline);
            const map = mapInstanceRef.current;
            if (!routePolylineRef.current) {
              routePolylineRef.current = new google.maps.Polyline({
                map,
                path: decodedPath,
                strokeColor: "#00E676",
                strokeWeight: 6,
                strokeOpacity: 0.85
              });
            } else {
              routePolylineRef.current.setPath(decodedPath);
            }
            const bounds = new google.maps.LatLngBounds();
            decodedPath.forEach((p) => bounds.extend(p));
            map.fitBounds(bounds, 60);

            setRouteInfo({
              distance: route.distanceKm != null ? `${route.distanceKm} km` : "",
              duration: formatDuration(route.durationSeconds)
            });
            setSteps(route.steps || []);
          } else {
            setMapError("Route preview available via native GPS navigation.");
          }
        } catch {
          if (!cancelled) {
            setLoading(false);
            setMapError("Route preview available via native GPS navigation.");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
          setMapError("Map initialization unavailable. Click 'Launch Turn-by-Turn GPS' below.");
        }
      }
    }

    initMap();

    return () => {
      cancelled = true;
    };
  }, [originLat, originLng, targetLocation.lat, targetLocation.lng, order.status]);

  return (
    <div className="goboot-gps-card">
      <div className="goboot-gps-header">
        <div className="goboot-gps-title">
          <span className="gps-badge">🤖 GOGO GPS AI</span>
          <h4>{isPickupStage ? "Stage 1: Heading to Pickup" : "Stage 2: En Route to Delivery"}</h4>
        </div>
        <button
          type="button"
          className="btn-native-gps"
          onClick={openNativeNavigation}
          title="Launch turn-by-turn voice navigation in Google Maps"
        >
          🧭 Launch Turn-by-Turn GPS
        </button>
      </div>

      {/* Goboot AI Advice Banner */}
      <div className="goboot-ai-banner">
        <div className="ai-banner-icon">🤖</div>
        <div className="ai-banner-text">
          <strong>Goboot GPS AI Insights:</strong>{" "}
          {routeInfo ? (
            <>
              Navigating to <strong>{targetLabel}</strong> ({targetLocation.address}). Estimated travel:{" "}
              <span className="highlight-text">{routeInfo.distance}</span> ({routeInfo.duration}). Traffic flow normal.
            </>
          ) : (
            <>
              Navigating rider to <strong>{targetLabel}</strong> ({targetLocation.address}). Live position active.
            </>
          )}

          {/* Active Stage Contact Person Quick Dial */}
          {isPickupStage && order.pickupContact?.name && (
            <div style={{ marginTop: "6px", fontSize: "13px" }}>
              👤 Pickup Contact: <strong>{order.pickupContact.name}</strong>
              {order.pickupContact.phone && (
                <a href={`tel:${order.pickupContact.phone}`} style={{ marginLeft: "8px", color: "var(--success-hover)", fontWeight: 700, textDecoration: "none", background: "rgba(41, 218, 150,0.15)", padding: "2px 8px", borderRadius: "4px" }}>
                  📞 Call {order.pickupContact.phone}
                </a>
              )}
            </div>
          )}
          {!isPickupStage && order.dropoffContact?.name && (
            <div style={{ marginTop: "6px", fontSize: "13px" }}>
              🏁 Drop-off Contact: <strong>{order.dropoffContact.name}</strong>
              {order.dropoffContact.phone && (
                <a href={`tel:${order.dropoffContact.phone}`} style={{ marginLeft: "8px", color: "#38bdf8", fontWeight: 700, textDecoration: "none", background: "rgba(56,189,248,0.15)", padding: "2px 8px", borderRadius: "4px" }}>
                  📞 Call {order.dropoffContact.phone}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Map Container */}
      <div className="map-wrapper" style={{ position: "relative", height: "300px", borderRadius: "12px", overflow: "hidden", marginBottom: "12px" }}>
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
        {loading && (
          <div className="map-overlay-loading">
            <span className="spin-icon">🔄</span> Calculating optimal route...
          </div>
        )}
        {mapError && (
          <div className="map-overlay-fallback">
            <p>{mapError}</p>
            <button type="button" className="btn primary" onClick={openNativeNavigation}>
              🗺️ Open Route in Google Maps
            </button>
          </div>
        )}
      </div>

      {/* Route Quick Summary & Turn-by-Turn Steps Toggle */}
      {routeInfo && (
        <div className="route-stats-bar">
          <div className="stat-pill">
            <span className="label">Remaining Distance</span>
            <span className="val">{routeInfo.distance}</span>
          </div>
          <div className="stat-pill">
            <span className="label">Est. Driving Time</span>
            <span className="val">{routeInfo.duration}</span>
          </div>
          <div className="stat-pill">
            <span className="label">Target</span>
            <span className="val">{targetLabel}</span>
          </div>
        </div>
      )}

      {/* Step-by-Step Directions */}
      {steps.length > 0 && (
        <div className="turn-by-turn-container">
          <button
            type="button"
            className="btn-toggle-steps"
            onClick={() => setShowSteps(!showSteps)}
          >
            {showSteps ? "▼ Hide Turn-by-Turn Steps" : `▶ View Step-by-Step Turn Directions (${steps.length} turns)`}
          </button>

          {showSteps && (
            <div className="turn-steps-list">
              {steps.map((step, idx) => (
                <div key={idx} className="turn-step-item">
                  <span className="step-num">{idx + 1}</span>
                  <div className="step-content">
                    <div className="step-text" style={{ whiteSpace: "pre-line" }}>{step.instruction}</div>
                    <span className="step-dist">
                      {step.distanceMeters >= 1000
                        ? `${(step.distanceMeters / 1000).toFixed(1)} km`
                        : `${step.distanceMeters} m`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
